import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { catchError, map, of, switchMap } from 'rxjs';
import { latestRequest, RequestStatus } from '../../state/latest-request';
import { UserService } from '../admins/user.service';

export interface HeaderUserView {
  displayName: string;
  email: string;
  phone: string;
  initials: string;
}

function authRecord(raw: unknown): Record<string, unknown> {
  if (!raw || typeof raw !== 'object') return {};
  if ('userData' in raw && raw.userData && typeof raw.userData === 'object')
    return raw.userData as Record<string, unknown>;
  return raw as Record<string, unknown>;
}

export function headerUserView(raw: unknown): HeaderUserView {
  const data = authRecord(raw);
  const text = (...keys: string[]): string => {
    for (const key of keys) if (typeof data[key] === 'string' && data[key]) return data[key];
    return '';
  };
  const rawName = text('name').trim();
  const nameParts = rawName.includes('@') ? [] : rawName.split(/\s+/).filter(Boolean);
  const first = text('firstName', 'given_name') || nameParts[0] || '';
  const last = text('lastName', 'family_name') || nameParts.slice(1).join(' ') || '';
  const email = text('email', 'username').trim().toLowerCase();
  const emailName = (email.split('@')[0]?.trim() || '')
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
  const displayName = [first, last].filter(Boolean).join(' ') || nameParts.join(' ') || emailName || 'Profile';
  const words = displayName.trim().split(/\s+/).filter(Boolean);
  const initials =
    (words.length > 1 ? words.map((word) => word[0]) : [displayName[0], displayName[1]])
      .filter(Boolean)
      .join('')
      .slice(0, 2)
      .toUpperCase() || 'SS';
  return {
    displayName,
    email,
    phone: text('phone', 'phoneNumber', 'phone_number', 'custom:phone', 'custom:phone_number'),
    initials,
  };
}

export const HeaderStore = signalStore(
  withState(() => ({ user: headerUserView(null), request: { loading: false, error: null } as RequestStatus })),
  withMethods((store, oidc = inject(OidcSecurityService), users = inject(UserService)) => ({
    load: latestRequest({
      request: (_: void) =>
        oidc.getUserData().pipe(
          switchMap((raw: unknown) => {
            const auth = authRecord(raw);
            return typeof auth['sub'] === 'string' && auth['sub']
              ? users.getUser(auth['sub']).pipe(
                  map((profile) => ({ ...auth, ...profile })),
                  catchError(() => of(auth)),
                )
              : of(auth);
          }),
          map(headerUserView),
        ),
      success: (user) => patchState(store, { user }),
      status: (request) => patchState(store, { request }),
      errorMessage: 'Unable to load profile.',
    }),
  })),
);
